import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AnseraLogo from "assets/images/Anser.png";
import SoftBox from "components/SoftBox";
import { useSettings } from "context/SettingsContext";
import { useSystemTheme } from "context/SystemThemeContext";

const RecaptchaSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";

  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState({
    siteKey: "",
    secretKey: "",
    message: "",
  });
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(false);

  const { settings, updateSettings, loading: settingsLoading } = useSettings();

  useEffect(() => {
    if (settings) {
      const formData = {
        siteKey: settings.ansera_search_google_recaptcha_v3_key_id || "",
        secretKey: settings.ansera_search_google_recaptcha_v3_key_secret || "",
        message: settings.ansera_search_suspicious_message || "",
      };

      setForm(formData);
      setInitialForm(formData);
      setEnabled(settings.ansera_search_google_recaptcha_v3_enable === 1);
    }
  }, [settings]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const hasChanges =
    initialForm &&
    (form.siteKey !== initialForm.siteKey ||
      form.secretKey !== initialForm.secretKey ||
      form.message !== initialForm.message ||
      enabled !== (settings?.ansera_search_google_recaptcha_v3_enable === 1));

  const handleSave = async () => {
    try {
      setLoading(true);

      const updates = {
        ansera_search_google_recaptcha_v3_key_id: form.siteKey,
        ansera_search_google_recaptcha_v3_key_secret: form.secretKey,
        ansera_search_suspicious_message: form.message,
        ansera_search_google_recaptcha_v3_enable: enabled ? 1 : 0,
      };

      await updateSettings(updates);
      setInitialForm({ ...form });
      alert("Settings updated successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      alert("Failed to save settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading && !settings) {
    return (
      <Box
        sx={{
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
          borderRadius: "16px",
          boxShadow: isDarkMode 
            ? "0 4px 20px rgba(0, 0, 0, 0.3)" 
            : "0 4px 20px rgba(0, 0, 0, 0.06)",
          p: 4,
          textAlign: "center",
          py: 4,
        }}
      >
        <Typography sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}>
          Loading settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#fff",
        borderRadius: "16px",
        boxShadow: isDarkMode 
          ? "0 4px 20px rgba(0, 0, 0, 0.3)" 
          : "0 4px 20px rgba(0, 0, 0, 0.06)",
        p: 4,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 5,
        fontFamily: theme.typography.fontFamily,
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      <Box flex={1}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <img 
            src={AnseraLogo} 
            alt="Ansera Logo" 
            width={64} 
            height={30}
            style={{
              filter: isDarkMode ? "brightness(0) invert(1)" : "none",
              transition: "filter 0.3s ease",
            }}
          />
          <Typography 
            sx={{ 
              fontWeight: 600, 
              fontSize: "1.125rem",
              color: isDarkMode ? "#e2e8f0" : "#344767",
              letterSpacing: "-0.01em"
            }}
          >
            Google reCAPTCHA
          </Typography>
        </Box>

        <Typography
          sx={{ 
            fontSize: "0.875rem", 
            lineHeight: 1.6,
            color: isDarkMode ? "#a0aec0" : "#67748e",
            mb: 3
          }}
        >
          Google reCAPTCHA v3 helps protect your website from bots by analyzing user behavior and
          assigning a risk score—without interrupting the user experience.
        </Typography>

        <Divider sx={{ 
          mb: 3, 
          borderColor: isDarkMode ? "#1a2542" : "#e5e7eb" 
        }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={enabled}
              onChange={() => setEnabled((prev) => !prev)}
              size="small"
              sx={{
                color: isDarkMode ? "#a0aec0" : "#219297",
                "&.Mui-checked": {
                  color: "#219297",
                }
              }}
            />
          }
          label={
            <Typography sx={{ 
              fontSize: "0.875rem", 
              fontWeight: 500, 
              color: isDarkMode ? "#e2e8f0" : "#344767" 
            }}>
              Use Google reCAPTCHA v3
            </Typography>
          }
          sx={{ mb: 1.5 }}
        />

        <Typography
          sx={{ 
            ml: 4, 
            fontSize: "0.8125rem", 
            color: isDarkMode ? "#a0aec0" : "#67748e", 
            display: "block", 
            mb: 3,
            lineHeight: 1.5,
            "& a": {
              color: "#219297",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline"
              }
            }
          }}
        >
          This plugin is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{" "}
          apply.
        </Typography>

        {[
          { label: "Google reCAPTCHA v3 Site Key", name: "siteKey", subtitle: "Client ID" },
          { label: "Google reCAPTCHA v3 Secret Key", name: "secretKey", subtitle: "Client Secret" },
          { label: "Recaptcha Suspicious Activity Message", name: "message", subtitle: "" },
        ].map(({ label, name, subtitle }) => (
          <Box key={name} mb={3}>
            <Typography 
              sx={{ 
                fontWeight: 500, 
                fontSize: "0.875rem",
                color: isDarkMode ? "#e2e8f0" : "#344767",
                mb: 0.5
              }}
            >
              {label}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: "0.8125rem",
                color: isDarkMode ? "#a0aec0" : "#67748e",
                mb: 1
              }}
            >
              {subtitle}
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              name={name}
              value={form[name]}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: "0.875rem",
                  backgroundColor: isDarkMode ? '#060C18 !important' : '#f8f9fa',
                  borderRadius: "8px",
                  '& fieldset': {
                    borderColor: isDarkMode ? '#4a5568 !important' : '#e9ecef',
                  },
                  '&:hover fieldset': {
                    borderColor: '#219297 !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#219297 !important',
                  },
                },
                '& .MuiInputBase-input': {
                  color: isDarkMode ? '#e2e8f0 !important' : '#344767',
                  '&::placeholder': {
                    color: isDarkMode ? '#a0aec0 !important' : '#9e9e9e',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        ))}

        <SoftBox mt={1}>
          <Button
            sx={{
              backgroundColor: "#219297",
              color: "#fff",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: "8px",
              boxShadow: "0 3px 10px rgba(33, 146, 151, 0.2)",
              "&:hover": { 
                backgroundColor: "#1b7d80",
                boxShadow: "0 5px 15px rgba(33, 146, 151, 0.3)",
              },
              "&:disabled": {
                backgroundColor: isDarkMode ? "#2d2d3f" : "#e9ecef",
                color: isDarkMode ? "#a0aec0" : "#adb5bd",
                boxShadow: "none",
              }
            }}
            onClick={handleSave}
            disabled={!hasChanges || loading || settingsLoading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </SoftBox>
      </Box>

      <Box 
        flex={1} 
        sx={{ 
          backgroundColor: isDarkMode ? "#0a1020" : "#f8f9fa",
          borderRadius: "12px",
          p: 3,
          transition: "background-color 0.3s ease",
        }}
      >
        <Typography 
          sx={{ 
            fontWeight: 600, 
            mb: 2.5, 
            fontSize: "1rem",
            color: isDarkMode ? "#e2e8f0" : "#344767"
          }}
        >
          How to Create Google reCAPTCHA Keys
        </Typography>

        <Typography
          component="ol"
          sx={{ 
            pl: 2.5, 
            fontSize: "0.875rem", 
            lineHeight: 1.7, 
            color: isDarkMode ? "#a0aec0" : "#67748e",
            "& li": {
              mb: 1.5,
              pl: 0.5
            },
            "& a": {
              color: "#219297",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline"
              }
            },
            "& b": {
              color: isDarkMode ? "#e2e8f0" : "#344767",
              fontWeight: 600
            },
            "& code": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.8125rem",
              fontFamily: "monospace",
              border: `1px solid ${isDarkMode ? "#4a5568" : "#e9ecef"}`,
              color: isDarkMode ? "#e2e8f0" : undefined,
            },
            "& ul": {
              mt: 1,
              mb: 0,
              color: isDarkMode ? "#a0aec0" : "#67748e",
            }
          }}
        >
          <li>
            Go to the{" "}
            <a href="https://www.google.com/recaptcha/admin/create" target="_blank" rel="noreferrer">
              reCAPTCHA Admin Console
            </a>
          </li>
          <li>Sign in with your Google account</li>
          <li>
            If you don't have a Google Cloud project, Google will prompt you to create one:
            <ul>
              <li>Click <b>Create</b> when asked to select a project.</li>
              <li>Give your project a name (e.g., "My Website Security").</li>
              <li>Click <b>Create</b> to continue</li>
            </ul>
          </li>
          <li>
            Choose <b>reCAPTCHA v3</b> as the type
          </li>
          <li>
            Enter your domain (e.g., <code>example.com</code> without http/https)
          </li>
          <li>Accept the terms and submit</li>
          <li>
            After submission, you will receive:
            <ul>
              <li><b>Site Key</b> – for frontend (public key)</li>
              <li><b>Secret Key</b> – for backend validation</li>
            </ul>
          </li>
          <li>
            Copy the keys and paste them in the fields on the left. Also, a message to the user will be shown if the user is suspected of being a bot.
          </li>
        </Typography>
      </Box>
    </Box>
  );
};

export default RecaptchaSettings;